import { STATUSPAGE } from '@repo/ui';
import {
  DeleteUserUseCaseInterface,
  CreateQuestionOptionUseCaseInterface,
  GetQuestionOptionByIdUseCaseInterface,
  UpdateQuestionOptionUseCaseInterface,
  ReorderQuestionOptionsUseCaseInterface,
  GetQuestionByIdUseCaseInterface,
} from 'domain/usecases';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSelectedQuestionOption,
  loadQuestionOptions,
  loadSelectedQuestionOption,
} from 'presentation/config/store/evaluation-criteria/question-options/question-options-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

export const useQuestionOptions = () => {
  // States
  const [createOrUpdateStatus, setCreateOrUpdateStatus] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [getByIdStatus, setGetByIdStatus] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );

  const { questionOptions, selectedOption } = useAppSelector(
    (state) => state.QuestionOptions,
  );

  // Hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { questionsService, questionOptionsService } = useService();

  // Constants
  const areInputsAndBtnsDisabled =
    getByIdStatus === STATUSPAGE.LOADING ||
    createOrUpdateStatus === STATUSPAGE.LOADING;

  // Methods
  const requestClearSelectedQuestionOption = useCallback(() => {
    dispatch(clearSelectedQuestionOption());
  }, []);

  const requestGetAllQuestionOptions = useCallback(
    async (params: GetQuestionByIdUseCaseInterface.Params) => {
      await questionsService
        .getQuestionById(params)
        .then((res) => {
          dispatch(loadQuestionOptions(res.options));
        })
        .catch((err) => {
          notifyError(err.message);
        });
    },
    [],
  );

  const requestReorderQuestionOptions = useCallback(
    async (params: ReorderQuestionOptionsUseCaseInterface.Params) => {
      await questionOptionsService
        .reorderQuestionOptions(params)
        .then((res) => {
          notifySuccess(res.message);
        })
        .catch((res) => {
          notifyError(res);
        });
    },
    [],
  );

  const requestCreateQuestionOption = useCallback(
    async (params: CreateQuestionOptionUseCaseInterface.Params) => {
      setCreateOrUpdateStatus(STATUSPAGE.LOADING);
      await questionOptionsService
        .createQuestionOption(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllQuestionOptions({ id: Number(id) });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setCreateOrUpdateStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetQuestionOptionById = useCallback(
    async (params: GetQuestionOptionByIdUseCaseInterface.Params) => {
      setGetByIdStatus(STATUSPAGE.LOADING);
      await questionOptionsService
        .getQuestionOptionById(params)
        .then((res) => {
          dispatch(loadSelectedQuestionOption(res));
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setGetByIdStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestUpdateQuestionOption = useCallback(
    async (
      params: UpdateQuestionOptionUseCaseInterface.Params,
      toggleModalEdit: () => void,
    ) => {
      setCreateOrUpdateStatus(STATUSPAGE.LOADING);
      await questionOptionsService
        .updateQuestionOption(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllQuestionOptions({ id: Number(id) });
          toggleModalEdit();
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setCreateOrUpdateStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestDeleteQuestionOption = useCallback(
    async (params: DeleteUserUseCaseInterface.Params) => {
      await questionOptionsService
        .deleteQuestionOption(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllQuestionOptions({ id: Number(id) });
        })
        .catch((err) => {
          notifyError(err.message || 'Não foi possível excluir a questão');
        });
    },
    [],
  );

  return {
    // states & data
    navigate,
    getByIdStatus,
    createOrUpdateStatus,
    areInputsAndBtnsDisabled,
    questionOptions,
    selectedOption,

    // methods
    requestCreateQuestionOption,
    requestDeleteQuestionOption,
    requestUpdateQuestionOption,
    requestGetQuestionOptionById,
    requestReorderQuestionOptions,
    requestGetAllQuestionOptions,
    requestClearSelectedQuestionOption,
  };
};
